/**
 * @fileoverview Modal component for creating and editing Quick Reaction (Reacción Rápida 1 vs 1) themes.
 * Supports tabbed navigation for Backgrounds & Logos, Player 1 Sprites (Fases 1..4), and Player 2 Sprites (Fases 1..4),
 * Drag & Drop file uploads with instant preview, asset removal/replacement, and validation with useFloatMessage.
 * 
 * @module components/Admin/Games/QuickReaction/QuickReactionThemeModal
 */

import React, { useState, useEffect } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from '../../../Providers/FloatMessage';
import {
    CreateThemeBL,
    UpdateThemeBL,
    UploadAssetBL,
    ValidateQuickReactionTheme,
    ValidateUploadFile,
    SanitizeAssetUrl,
    SanitizeRoomId,
    SanitizeText
} from '../../../../utilities/BL/QuickReaction';

/**
 * Resuelve y sanea la URL absoluta para previsualización de recursos multimedia.
 * Previene inyección de esquemas ejecutables (javascript:, vbscript:, etc.) y traversal.
 * 
 * @param {string} mediaPath - Ruta relativa o URL absoluta del archivo.
 * @returns {string} URL completa formateada y saneada.
 */
export const getFullAssetUrl = (mediaPath) => {
    if (!mediaPath) return '';
    const sanitizedPath = SanitizeAssetUrl(mediaPath);
    if (!sanitizedPath) return '';

    if (sanitizedPath.startsWith('http://') || sanitizedPath.startsWith('https://') || sanitizedPath.startsWith('blob:') || sanitizedPath.startsWith('data:')) {
        return sanitizedPath;
    }
    const apiBaseUrl = (process.env.REACT_APP_API_URL || 'https://localhost:50586/api/')
        .replace('/api/', '/')
        .replace('/api', '/');
    return `${apiBaseUrl}${sanitizedPath.startsWith('/') ? sanitizedPath.substring(1) : sanitizedPath}`;
};

/**
 * Componente Drag & Drop Reutilizable para campos individuales de multimedia con validación en cliente.
 * 
 * @function DropzoneField
 * @param {Object} props - Propiedades del componente.
 * @param {string} props.label - Etiqueta descriptiva del campo.
 * @param {string} props.fieldName - Clave del campo en el estado del formulario.
 * @param {string} props.value - URL o ruta actual del recurso.
 * @param {Function} props.onUpload - Función callback para procesar la subida del archivo.
 * @param {Function} props.onRemove - Función callback para limpiar el campo.
 * @param {boolean} [props.isUploading=false] - Indicador de carga activa.
 * @param {string} [props.aspectHint=''] - Recomendación de resolución o formato.
 * @param {string} [props.helpText=''] - Texto de ayuda contextual.
 * @param {string} [props.accept='image/png, image/jpeg, image/webp, image/svg+xml'] - Tipos MIME aceptados.
 * @returns {React.ReactElement} Campo de carga con drag & drop y preview.
 */
const DropzoneField = ({
    label,
    fieldName,
    value,
    onUpload,
    onRemove,
    isUploading = false,
    aspectHint = '',
    helpText = '',
    accept = 'image/png, image/jpeg, image/webp, image/svg+xml'
}) => {
    const { showMessage } = useFloatMessage();
    const [isDragHoverActive, setIsDragHoverActive] = useState(false);
    const inputElementId = `file-input-${fieldName}`;

    const processSelectedFile = (file) => {
        if (!file) return;

        // Validación defensiva en frontend (tamaño, extensión y MIME type)
        const validation = ValidateUploadFile(file);
        if (!validation.isValid) {
            showMessage({
                type: MESSAGE_TYPE.WARNING,
                text: validation.message
            });
            return;
        }

        onUpload(file, fieldName);
    };

    const handleDragOver = (dragEvent) => {
        dragEvent.preventDefault();
        dragEvent.stopPropagation();
        setIsDragHoverActive(true);
    };

    const handleDragLeave = (dragEvent) => {
        dragEvent.preventDefault();
        dragEvent.stopPropagation();
        setIsDragHoverActive(false);
    };

    const handleDrop = (dropEvent) => {
        dropEvent.preventDefault();
        dropEvent.stopPropagation();
        setIsDragHoverActive(false);
        const selectedFiles = dropEvent.dataTransfer.files;
        if (selectedFiles && selectedFiles.length > 0) {
            processSelectedFile(selectedFiles[0]);
        }
    };

    const handleNativeFileChange = (changeEvent) => {
        const selectedFiles = changeEvent.target.files;
        if (selectedFiles && selectedFiles.length > 0) {
            processSelectedFile(selectedFiles[0]);
            // Limpiar valor del input para permitir seleccionar el mismo archivo tras cambios
            changeEvent.target.value = '';
        }
    };

    return (
        <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
                <label className="form-label fw-bold text-dark small mb-0">
                    {label}
                </label>
                {aspectHint && (
                    <span className="badge bg-light text-muted border small">
                        {aspectHint}
                    </span>
                )}
            </div>

            <div
                className={`border border-2 rounded-3 p-3 text-center position-relative transition-all d-flex flex-column align-items-center justify-content-center ${
                    isDragHoverActive
                        ? 'border-primary bg-primary-subtle shadow-sm'
                        : 'border-secondary-subtle bg-light'
                }`}
                style={{
                    borderStyle: 'dashed',
                    minHeight: '110px',
                    cursor: isUploading ? 'not-allowed' : 'pointer'
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => {
                    if (!isUploading) {
                        const inputEl = document.getElementById(inputElementId);
                        if (inputEl) inputEl.click();
                    }
                }}
            >
                <input
                    id={inputElementId}
                    type="file"
                    className="d-none"
                    accept={accept}
                    onChange={handleNativeFileChange}
                    disabled={isUploading}
                />

                {isUploading ? (
                    <div className="py-2 d-flex flex-column align-items-center">
                        <div className="spinner-border spinner-border-sm text-primary mb-2" role="status"></div>
                        <span className="small text-primary fw-bold">Optimizando y subiendo archivo...</span>
                    </div>
                ) : value ? (
                    <div
                        className="position-relative d-flex flex-column align-items-center w-100"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={getFullAssetUrl(value)}
                            alt="Vista previa"
                            className="img-thumbnail shadow-sm bg-white mb-2"
                            style={{
                                maxHeight: '90px',
                                maxWidth: '100%',
                                objectFit: 'contain'
                            }}
                            onError={(errorEvent) => {
                                errorEvent.target.style.display = 'none';
                            }}
                        />
                        <div className="d-flex gap-2 align-items-center">
                            <span
                                className="small text-truncate text-muted"
                                style={{ maxWidth: '200px' }}
                                title={value}
                            >
                                {value.split('/').pop()}
                            </span>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger py-0 px-2"
                                title="Eliminar archivo"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRemove(fieldName);
                                }}
                            >
                                <i className="bi bi-trash3"></i>
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary py-0 px-2"
                                title="Reemplazar archivo"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const inputEl = document.getElementById(inputElementId);
                                    if (inputEl) inputEl.click();
                                }}
                            >
                                <i className="bi bi-arrow-repeat"></i>
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <i className="bi bi-cloud-arrow-up text-primary fs-3 d-block mb-1"></i>
                        <span className="small text-muted d-block">
                            Arrastra aquí tu archivo o haz <strong className="text-primary">clic para buscar</strong>
                        </span>
                        {helpText && (
                            <small className="text-secondary d-block mt-1 font-monospace" style={{ fontSize: '0.75rem' }}>
                                {helpText}
                            </small>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

/**
 * Modal para la creación, edición y personalización de temas de Reacción Rápida (1 vs 1).
 * 
 * @function QuickReactionThemeModal
 * @param {Object} props - Propiedades del modal.
 * @param {Function} props.onClose - Callback para cerrar el modal.
 * @param {Function} props.onThemeSaved - Callback tras guardar exitosamente el tema.
 * @param {Object|null} [props.themeToEdit=null] - Objeto tema a editar o nulo para nuevo.
 * @param {string|null} [props.roomId=null] - Identificador GUID de la sala activa.
 * @param {string|null} [props.operatorId=null] - Identificador GUID del operador.
 * @returns {React.ReactElement} Estructura modal interactiva.
 */
export const QuickReactionThemeModal = ({
    onClose,
    onThemeSaved,
    themeToEdit = null,
    roomId = null,
    operatorId = null
}) => {
    const { showMessage } = useFloatMessage();

    // Pestaña activa ('backgrounds', 'spritesJ1', 'spritesJ2')
    const [activeTabKey, setActiveTabKey] = useState('backgrounds');

    // Estado del formulario
    const [themeFormState, setThemeFormState] = useState({
        id: null,
        name: '',
        roomId: roomId || null,
        operatorId: operatorId || null,
        isSystemPreset: false,
        screenBackgroundImageUrl: '',
        mobileBackgroundImageUrl: '',
        logoImageUrl: '',
        j1SpriteBackUrl: '',
        j1SpriteFrontUrl: '',
        j1SpriteShootUrl: '',
        j1SpriteVictoryUrl: '',
        j2SpriteBackUrl: '',
        j2SpriteFrontUrl: '',
        j2SpriteShootUrl: '',
        j2SpriteVictoryUrl: ''
    });

    // Estado de carga por campo
    const [uploadingFieldKey, setUploadingFieldKey] = useState(null);
    const [isSavingInProgress, setIsSavingInProgress] = useState(false);

    // Inicializar estado con tema a editar si existe
    useEffect(() => {
        if (themeToEdit && typeof themeToEdit === 'object') {
            setThemeFormState({
                id: themeToEdit.id || themeToEdit.Id || null,
                name: themeToEdit.name || themeToEdit.Name || '',
                roomId: themeToEdit.roomId || themeToEdit.RoomId || roomId || null,
                operatorId: themeToEdit.operatorId || themeToEdit.OperatorId || operatorId || null,
                isSystemPreset: Boolean(themeToEdit.isSystemPreset || themeToEdit.IsSystemPreset),
                screenBackgroundImageUrl: themeToEdit.screenBackgroundImageUrl || themeToEdit.ScreenBackgroundImageUrl || '',
                mobileBackgroundImageUrl: themeToEdit.mobileBackgroundImageUrl || themeToEdit.MobileBackgroundImageUrl || '',
                logoImageUrl: themeToEdit.logoImageUrl || themeToEdit.LogoImageUrl || '',
                j1SpriteBackUrl: themeToEdit.j1SpriteBackUrl || themeToEdit.J1SpriteBackUrl || '',
                j1SpriteFrontUrl: themeToEdit.j1SpriteFrontUrl || themeToEdit.J1SpriteFrontUrl || '',
                j1SpriteShootUrl: themeToEdit.j1SpriteShootUrl || themeToEdit.J1SpriteShootUrl || '',
                j1SpriteVictoryUrl: themeToEdit.j1SpriteVictoryUrl || themeToEdit.J1SpriteVictoryUrl || '',
                j2SpriteBackUrl: themeToEdit.j2SpriteBackUrl || themeToEdit.J2SpriteBackUrl || '',
                j2SpriteFrontUrl: themeToEdit.j2SpriteFrontUrl || themeToEdit.J2SpriteFrontUrl || '',
                j2SpriteShootUrl: themeToEdit.j2SpriteShootUrl || themeToEdit.J2SpriteShootUrl || '',
                j2SpriteVictoryUrl: themeToEdit.j2SpriteVictoryUrl || themeToEdit.J2SpriteVictoryUrl || ''
            });
        }
    }, [themeToEdit, roomId, operatorId]);

    /**
     * Sube un archivo y actualiza el campo correspondiente del formulario tras validación defensiva.
     */
    const handleUploadFile = async (fileObject, targetFieldName) => {
        if (!fileObject) return;

        const fileValidation = ValidateUploadFile(fileObject);
        if (!fileValidation.isValid) {
            showMessage({
                type: MESSAGE_TYPE.WARNING,
                text: fileValidation.message
            });
            return;
        }

        setUploadingFieldKey(targetFieldName);
        try {
            const sanitizedRoomId = SanitizeRoomId(roomId);
            const uploadResponse = await UploadAssetBL(fileObject, sanitizedRoomId);
            if (uploadResponse.success && uploadResponse.url) {
                const cleanUrl = SanitizeAssetUrl(uploadResponse.url);
                setThemeFormState((prevState) => ({
                    ...prevState,
                    [targetFieldName]: cleanUrl
                }));
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: 'Recurso subido y optimizado exitosamente.'
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: uploadResponse.message || 'No fue posible subir el archivo seleccionado.'
                });
            }
        } catch (uploadError) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error inesperado durante la subida del archivo.'
            });
        } finally {
            setUploadingFieldKey(null);
        }
    };

    /**
     * Limpia el valor de un campo de imagen en el formulario.
     */
    const handleRemoveFile = (targetFieldName) => {
        setThemeFormState((prevState) => ({
            ...prevState,
            [targetFieldName]: ''
        }));
    };

    /**
     * Guarda o actualiza el tema visual tras validación estricta y saneamiento.
     */
    const handleFormSubmit = async (submitEvent) => {
        submitEvent.preventDefault();

        // Validación estricta con BL
        const validationResult = ValidateQuickReactionTheme(themeFormState);
        if (!validationResult.isValid) {
            showMessage({
                type: MESSAGE_TYPE.WARNING,
                text: validationResult.message || 'Por favor completa los campos requeridos.'
            });
            setActiveTabKey('backgrounds');
            return;
        }

        setIsSavingInProgress(true);
        showMessage({
            type: MESSAGE_TYPE.INFO,
            text: 'Guardando configuración del tema...'
        });

        try {
            const isEditing = Boolean(themeFormState.id);
            const effectiveRoomId = SanitizeRoomId(themeFormState.roomId || roomId);
            const sanitizedName = SanitizeText(themeFormState.name, 200);

            const payloadToSubmit = {
                ...themeFormState,
                name: sanitizedName,
                roomId: effectiveRoomId || null
            };

            const response = isEditing
                ? await UpdateThemeBL(themeFormState.id, payloadToSubmit, effectiveRoomId)
                : await CreateThemeBL(payloadToSubmit, effectiveRoomId);

            if (response.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: isEditing
                        ? `Tema "${sanitizedName}" actualizado exitosamente.`
                        : `Tema "${sanitizedName}" creado exitosamente.`
                });
                if (typeof onThemeSaved === 'function') {
                    onThemeSaved(response.data || payloadToSubmit);
                }
                onClose();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response.message || 'Error al guardar el tema.'
                });
            }
        } catch (saveError) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al persistir el tema.'
            });
        } finally {
            setIsSavingInProgress(false);
        }
    };

    return (
        <>
            <div className="cModal__Background" onClick={onClose}></div>
            <div className="cModal__Container" style={{ zIndex: 99999, maxWidth: '980px', width: '95vw' }}>
                <div
                    className="cModal__Container__Content bg-white rounded-4 shadow-lg p-0 overflow-hidden text-dark position-relative"
                    style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
                >
                    {/* Modal Header */}
                    <div className="d-flex justify-content-between align-items-center p-3 px-4 bg-dark text-white border-bottom border-secondary">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-lightning-charge-fill text-warning fs-4"></i>
                            <h5 className="m-0 fw-bold">
                                {themeFormState.id ? `Editar Tema: ${themeFormState.name}` : 'Crear Nuevo Tema - Reacción Rápida'}
                            </h5>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-light rounded-circle"
                            onClick={onClose}
                            title="Cerrar modal"
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>

                    {/* Navigation Tabs Header */}
                    <div className="bg-light border-bottom px-3 pt-2">
                        <ul className="nav nav-tabs border-0 flex-nowrap overflow-x-auto">
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${
                                        activeTabKey === 'backgrounds'
                                            ? 'active text-primary border-bottom-0'
                                            : 'text-secondary'
                                    }`}
                                    onClick={() => setActiveTabKey('backgrounds')}
                                    style={{ minHeight: '44px' }}
                                >
                                    <i className="bi bi-image me-1"></i> 1. Fondos & Logos
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${
                                        activeTabKey === 'spritesJ1'
                                            ? 'active text-primary border-bottom-0'
                                            : 'text-secondary'
                                    }`}
                                    onClick={() => setActiveTabKey('spritesJ1')}
                                    style={{ minHeight: '44px' }}
                                >
                                    <i className="bi bi-person-standing me-1 text-primary"></i> 2. Sprites Lado A (J1)
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${
                                        activeTabKey === 'spritesJ2'
                                            ? 'active text-primary border-bottom-0'
                                            : 'text-secondary'
                                    }`}
                                    onClick={() => setActiveTabKey('spritesJ2')}
                                    style={{ minHeight: '44px' }}
                                >
                                    <i className="bi bi-person-standing me-1 text-danger"></i> 3. Sprites Lado B (J2)
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* Scrollable Form Body */}
                    <form onSubmit={handleFormSubmit} className="d-flex flex-column flex-grow-1 overflow-hidden m-0 p-0">
                        <div className="p-4 overflow-y-auto flex-grow-1" style={{ maxHeight: 'calc(90vh - 170px)' }}>
                            {/* ============================================================ */}
                            {/* TAB 1: FONDOS & LOGOS                                         */}
                            {/* ============================================================ */}
                            {activeTabKey === 'backgrounds' && (
                                <div>
                                    <div className="row g-3 mb-3">
                                        <div className="col-12">
                                            <label className="form-label fw-bold text-dark mb-1">
                                                Nombre del Tema <span className="text-danger">*</span>:
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control form-control-lg border-2"
                                                placeholder="Ej. Duelo del Viejo Oeste / Cyberpunk Neon"
                                                value={themeFormState.name}
                                                onChange={(e) =>
                                                    setThemeFormState({
                                                        ...themeFormState,
                                                        name: e.target.value
                                                    })
                                                }
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="row g-3">
                                        <div className="col-12 col-md-6">
                                            <DropzoneField
                                                label="Fondo Pantalla Gigante (1920x1080)"
                                                fieldName="screenBackgroundImageUrl"
                                                value={themeFormState.screenBackgroundImageUrl}
                                                onUpload={handleUploadFile}
                                                onRemove={handleRemoveFile}
                                                isUploading={uploadingFieldKey === 'screenBackgroundImageUrl'}
                                                aspectHint="1920x1080 (16:9)"
                                                helpText="Formato recomendado: JPG, WebP o PNG (Full HD 1920x1080)"
                                            />
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <DropzoneField
                                                label="Fondo Vista Móvil Participantes (1080x1920)"
                                                fieldName="mobileBackgroundImageUrl"
                                                value={themeFormState.mobileBackgroundImageUrl}
                                                onUpload={handleUploadFile}
                                                onRemove={handleRemoveFile}
                                                isUploading={uploadingFieldKey === 'mobileBackgroundImageUrl'}
                                                aspectHint="1080x1920 (9:16)"
                                                helpText="Formato recomendado: JPG, WebP o PNG (Vertical móvil)"
                                            />
                                        </div>

                                        <div className="col-12">
                                            <DropzoneField
                                                label="Logotipo del Tema / Patrocinador (Opcional)"
                                                fieldName="logoImageUrl"
                                                value={themeFormState.logoImageUrl}
                                                onUpload={handleUploadFile}
                                                onRemove={handleRemoveFile}
                                                isUploading={uploadingFieldKey === 'logoImageUrl'}
                                                aspectHint="PNG / WebP transparente"
                                                helpText="Logo o marca que se mostrará en la portada y escena de duelo"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ============================================================ */}
                            {/* TAB 2: SPRITES LADO A (J1 - IZQUIERDA)                       */}
                            {/* ============================================================ */}
                            {activeTabKey === 'spritesJ1' && (
                                <div>
                                    <div className="alert alert-primary py-2 px-3 mb-3 d-flex align-items-center gap-2">
                                        <i className="bi bi-info-circle-fill fs-5"></i>
                                        <span className="small">
                                            Configura las 4 fases de animación para el <strong>Jugador 1 (Lado A)</strong>. Se recomienda formato PNG o WebP con fondo transparente.
                                        </span>
                                    </div>

                                    <div className="row g-3">
                                        <div className="col-12 col-md-6">
                                            <DropzoneField
                                                label="1. Fase Espaldas (Esperando señal)"
                                                fieldName="j1SpriteBackUrl"
                                                value={themeFormState.j1SpriteBackUrl}
                                                onUpload={handleUploadFile}
                                                onRemove={handleRemoveFile}
                                                isUploading={uploadingFieldKey === 'j1SpriteBackUrl'}
                                                aspectHint="PNG / WebP transparente"
                                                helpText="Sprite de espaldas esperando la señal aleatoria"
                                            />
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <DropzoneField
                                                label="2. Fase Frente (Darse la vuelta)"
                                                fieldName="j1SpriteFrontUrl"
                                                value={themeFormState.j1SpriteFrontUrl}
                                                onUpload={handleUploadFile}
                                                onRemove={handleRemoveFile}
                                                isUploading={uploadingFieldKey === 'j1SpriteFrontUrl'}
                                                aspectHint="PNG / WebP transparente"
                                                helpText="Sprite de frente al activarse la señal de disparo"
                                            />
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <DropzoneField
                                                label="3. Fase Disparo (Acción / Desenfunde)"
                                                fieldName="j1SpriteShootUrl"
                                                value={themeFormState.j1SpriteShootUrl}
                                                onUpload={handleUploadFile}
                                                onRemove={handleRemoveFile}
                                                isUploading={uploadingFieldKey === 'j1SpriteShootUrl'}
                                                aspectHint="PNG / WebP transparente"
                                                helpText="Sprite ejecutando el disparo o acción rápida"
                                            />
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <DropzoneField
                                                label="4. Fase Victoria (Celebración)"
                                                fieldName="j1SpriteVictoryUrl"
                                                value={themeFormState.j1SpriteVictoryUrl}
                                                onUpload={handleUploadFile}
                                                onRemove={handleRemoveFile}
                                                isUploading={uploadingFieldKey === 'j1SpriteVictoryUrl'}
                                                aspectHint="PNG / WebP transparente"
                                                helpText="Sprite celebrando el triunfo en la pantalla"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ============================================================ */}
                            {/* TAB 3: SPRITES LADO B (J2 - DERECHA)                         */}
                            {/* ============================================================ */}
                            {activeTabKey === 'spritesJ2' && (
                                <div>
                                    <div className="alert alert-danger py-2 px-3 mb-3 d-flex align-items-center gap-2">
                                        <i className="bi bi-info-circle-fill fs-5"></i>
                                        <span className="small">
                                            Configura las 4 fases de animación para el <strong>Jugador 2 (Lado B)</strong>. Se recomienda formato PNG o WebP con fondo transparente.
                                        </span>
                                    </div>

                                    <div className="row g-3">
                                        <div className="col-12 col-md-6">
                                            <DropzoneField
                                                label="1. Fase Espaldas (Esperando señal)"
                                                fieldName="j2SpriteBackUrl"
                                                value={themeFormState.j2SpriteBackUrl}
                                                onUpload={handleUploadFile}
                                                onRemove={handleRemoveFile}
                                                isUploading={uploadingFieldKey === 'j2SpriteBackUrl'}
                                                aspectHint="PNG / WebP transparente"
                                                helpText="Sprite de espaldas esperando la señal aleatoria"
                                            />
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <DropzoneField
                                                label="2. Fase Frente (Darse la vuelta)"
                                                fieldName="j2SpriteFrontUrl"
                                                value={themeFormState.j2SpriteFrontUrl}
                                                onUpload={handleUploadFile}
                                                onRemove={handleRemoveFile}
                                                isUploading={uploadingFieldKey === 'j2SpriteFrontUrl'}
                                                aspectHint="PNG / WebP transparente"
                                                helpText="Sprite de frente al activarse la señal de disparo"
                                            />
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <DropzoneField
                                                label="3. Fase Disparo (Acción / Desenfunde)"
                                                fieldName="j2SpriteShootUrl"
                                                value={themeFormState.j2SpriteShootUrl}
                                                onUpload={handleUploadFile}
                                                onRemove={handleRemoveFile}
                                                isUploading={uploadingFieldKey === 'j2SpriteShootUrl'}
                                                aspectHint="PNG / WebP transparente"
                                                helpText="Sprite ejecutando el disparo o acción rápida"
                                            />
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <DropzoneField
                                                label="4. Fase Victoria (Celebración)"
                                                fieldName="j2SpriteVictoryUrl"
                                                value={themeFormState.j2SpriteVictoryUrl}
                                                onUpload={handleUploadFile}
                                                onRemove={handleRemoveFile}
                                                isUploading={uploadingFieldKey === 'j2SpriteVictoryUrl'}
                                                aspectHint="PNG / WebP transparente"
                                                helpText="Sprite celebrando el triunfo en la pantalla"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="p-3 bg-light border-top d-flex justify-content-between align-items-center">
                            <button
                                type="button"
                                className="btn btn-outline-secondary px-4 fw-semibold"
                                onClick={onClose}
                                disabled={isSavingInProgress}
                                style={{ minHeight: '44px' }}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary px-4 fw-bold d-flex align-items-center gap-2"
                                disabled={isSavingInProgress}
                                style={{ minHeight: '44px' }}
                            >
                                {isSavingInProgress ? (
                                    <>
                                        <div className="spinner-border spinner-border-sm" role="status"></div>
                                        <span>Guardando...</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check-circle-fill"></i>
                                        <span>{themeFormState.id ? 'Actualizar Tema' : 'Guardar Tema'}</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default QuickReactionThemeModal;
